// Picker de formato para Interfaz V2.
//
// Dos modos de uso según el viewport:
//
//   • Mobile  → `FormatPicker` es un bottom sheet (portal a body).
//               Al tocar una opción se selecciona y cierra de una.
//   • Desktop → `FormatPickerInline` se renderiza inline en el
//               ConfigurePanel cuando el panelView === 'format', igual
//               al patrón del ModelPicker create/upload (la sub-vista
//               toma el panel completo, sin overlay aparte).

import React from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronLeft, X } from 'lucide-react';
import { FORMAT_OPTIONS_V2 } from './mockData';
import { useExitTransition } from '../../hooks/useExitTransition';

// ─── Sub: lista compartida ────────────────────────────────────────────
// Misma data renderizada en dos layouts según el host:
//   - 'grid'  (mobile bottom sheet) → grilla 2-col, preview cuadrado.
//   - 'rows'  (desktop inline)      → lista vertical, thumbnail + label.
// Click en una opción dispara `onPick(id)` y el host decide qué hacer.

interface FormatOptionsListProps {
  selectedId: string | null;
  onPick: (id: string) => void;
  layout: 'grid' | 'rows';
}

const FormatOptionsList: React.FC<FormatOptionsListProps> = ({
  selectedId,
  onPick,
  layout,
}) => {
  if (layout === 'grid') {
    return (
      <div className="grid grid-cols-2 gap-3">
        {FORMAT_OPTIONS_V2.map((opt) => {
          const isSel = selectedId === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onPick(opt.id)}
              className={`relative text-left rounded-2xl overflow-hidden bg-white border-2 transition-all active:scale-[0.98] ${
                isSel ? 'border-brand-primary' : 'border-neutral-200'
              }`}
            >
              <div className="relative aspect-square bg-neutral-100">
                <img
                  src={opt.imageUrl}
                  alt={opt.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {isSel && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                )}
              </div>
              <p className="text-[13px] text-brand-black/90 px-3 py-2.5 font-medium truncate">
                {opt.name}
              </p>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {FORMAT_OPTIONS_V2.map((opt) => {
        const isSel = selectedId === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onPick(opt.id)}
            className={`w-full text-left rounded-xl bg-white border p-3 flex items-center gap-4 transition-colors ${
              isSel
                ? 'border-brand-primary ring-2 ring-brand-primary/20'
                : 'border-neutral-200 hover:bg-neutral-50'
            }`}
          >
            <div className="w-16 h-16 rounded-md overflow-hidden bg-neutral-100 shrink-0">
              <img
                src={opt.imageUrl}
                alt={opt.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 flex items-center justify-between min-w-0">
              <p className="text-[14.5px] text-brand-black/90 font-medium truncate">
                {opt.name}
              </p>
              {isSel && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white shrink-0 ml-2">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ─── Mobile bottom sheet ──────────────────────────────────────────────

interface FormatPickerProps {
  open: boolean;
  selectedId: string | null;
  onChange: (id: string) => void;
  onClose: () => void;
}

export const FormatPicker: React.FC<FormatPickerProps> = ({
  open,
  selectedId,
  onChange,
  onClose,
}) => {
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false (al elegir un formato o tocar
  // cerrar) — así el sheet desliza inverso antes de desmontarse.
  const { mounted, animated } = useExitTransition(open, 300);

  if (!mounted) return null;

  // Tap en una opción → setea + cierra de una. No hay paso de "Aceptar"
  // porque el formato es una decisión simple y no queremos un tap extra.
  const handlePick = (id: string) => {
    onChange(id);
    onClose();
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      {/* Container responsive:
            • mobile  → bottom sheet (translate-y)
            • desktop → side panel deslizante desde la derecha (top-4
              right-4 bottom-4 w-[630px]), mismo patrón que PruebaNuevaInterfaz. */}
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          // En mobile la altura del sheet (en className) ya excluye el
          // safe area top — bottom-anclado sin zona blanca arriba.
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {/* Drag handle — solo mobile. */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Header iOS (mobile): Cancelar · título · spacer. */}
        <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Formato
          </h3>
          <div className="w-20" />
        </div>
        {/* Header desktop: título + X close. Divider abajo. */}
        <div className="hidden md:flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-brand-black/90">
            Formato
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hidden md:block mx-5 h-px bg-black/[0.08] shrink-0" />
        <div className="flex-1 overflow-y-auto px-4 md:px-5 pt-2 md:pt-4 pb-4">
          <FormatOptionsList
            selectedId={selectedId}
            onPick={handlePick}
            layout="grid"
          />
        </div>
      </div>
    </>,
    document.body,
  );
};

// ─── Desktop inline ───────────────────────────────────────────────────
// Para usar dentro del ConfigurePanel desktop. Cuando el user toca el
// chip de Formato, el panel cambia a esta vista — list completa de
// opciones, "Volver" arriba a la derecha, y tap en opción commitea +
// vuelve al main view (cb `onPick` se encarga de ambas cosas).

interface FormatPickerInlineProps {
  selectedId: string | null;
  onPick: (id: string) => void;
  onBack: () => void;
}

export const FormatPickerInline: React.FC<FormatPickerInlineProps> = ({
  selectedId,
  onPick,
  onBack,
}) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
          Formato
        </p>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Volver
        </button>
      </div>
      <FormatOptionsList
        selectedId={selectedId}
        onPick={onPick}
        layout="rows"
      />
    </div>
  );
};
